import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useTokens } from "../../lib/queries";
import { fmtAmt, fmtUsd, fmtPrice } from "../../lib/format";
import { SlippageSaved } from "./SlippageSaved";

export const Simulator = () => {
  const { data: tokens } = useTokens();
  const [symbol, setSymbol] = useState("");
  const [amount, setAmount] = useState("");
  const [disc, setDisc] = useState(5);
  const [res, setRes] = useState(null);
  const token = useMemo(() => tokens?.find((t) => t.symbol === symbol), [tokens, symbol]);
  useEffect(() => { if (!symbol && tokens?.[0]) { setSymbol(tokens[0].symbol); setAmount(String(Math.round(tokens[0].token_reserve * 0.2))); } }, [tokens, symbol]);

  const run = () => token && Number(amount) > 0 && api.slippage({ token_symbol: token.symbol, token_amount: Number(amount), price_usd: token.pool_price_usd * (1 - disc / 100) }).then(setRes);
  useEffect(() => { run(); }, [token, amount, disc]); // eslint-disable-line react-hooks/exhaustive-deps

  const curve = useMemo(() => {
    if (!token) return [];
    return [1, 2, 5, 10, 20, 30, 50, 75, 100].map((p) => {
      const x = token.token_reserve * p / 100;
      const out = token.quote_reserve_usd * x / (token.token_reserve + x);
      return { p, impact: (1 - (out / x) / token.pool_price_usd) * 100, out };
    });
  }, [token]);

  return (
    <section className="panel flex-1 min-h-0 flex flex-col max-lg:h-auto max-lg:flex-none" data-testid="simulator-container">
      <div className="panel-head"><h2 className="panel-title">Impact simulator</h2><span className="mono text-[11px] text-fog">constant-product · Uniswap v4 · stock-paired pools</span></div>
      <div className="flex-1 overflow-auto p-5 max-lg:p-4 grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
        <div className="space-y-4">
          <label className="block"><span className="eyebrow">Token · pair</span>
            <select value={symbol} onChange={(e) => { setSymbol(e.target.value); const t = tokens.find((x) => x.symbol === e.target.value); setAmount(String(Math.round(t.token_reserve * 0.2))); }} className="field mt-1" data-testid="calc-token-select">
              {(tokens || []).map((t) => <option key={t.symbol} value={t.symbol}>{`${t.symbol}/${t.stock} · ${fmtUsd(t.pool_depth_usd)} deep`}</option>)}
            </select>
          </label>
          <label className="block"><span className="eyebrow">Block size · tokens</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" className="field mt-1" data-testid="calc-trade-size-input" />
            <div className="flex gap-1 mt-1.5 flex-wrap">{[5, 10, 20, 35, 50].map((p) => <button key={p} onClick={() => setAmount(String(Math.round(token.token_reserve * p / 100)))} className="tag border-line2 text-fog hover:border-violet hover:text-white max-lg:h-8 max-lg:px-3 max-lg:text-[11px]" data-testid={`calc-share-${p}`}>{p}%</button>)}</div>
          </label>
          <label className="block"><span className="eyebrow">OTC discount vs pool price · {disc}%</span>
            <input type="range" min="0" max="25" step="0.5" value={disc} onChange={(e) => setDisc(Number(e.target.value))} className="w-full mt-2 accent-violet" data-testid="calc-discount-slider" />
            <div className="mono text-[11px] text-fog mt-1">OTC price {token ? fmtPrice(token.pool_price_usd * (1 - disc / 100)) : "—"}/M · pool {token ? fmtPrice(token.pool_price_usd) : "—"}</div>
          </label>
          {res && <SlippageSaved s={res} symbol={token.symbol} amount={Number(amount)} />}
        </div>
        <div>
          <div className="eyebrow mb-2">Price impact curve · {token?.symbol}/{token?.stock} · pool depth {token ? fmtUsd(token.pool_depth_usd) : "—"}</div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[540px] text-xs mono" data-testid="calc-curve-table">
            <thead><tr className="eyebrow text-left [&>th]:px-2 [&>th]:h-7 [&>th]:font-normal border-b border-line"><th>Sell size (% of pool reserve)</th><th className="text-right">Tokens</th><th className="text-right">USD out</th><th className="text-right">Price impact</th><th className="w-1/3"></th></tr></thead>
            <tbody>
              {curve.map((r) => (
                <tr key={r.p} className="border-b border-line/60 [&>td]:px-2 [&>td]:h-8">
                  <td className="text-slate-300">{r.p}%</td><td className="text-right text-fog">{fmtAmt(token.token_reserve * r.p / 100)}</td><td className="text-right">{fmtUsd(r.out)}</td>
                  <td className={`text-right font-medium ${r.impact > 20 ? "text-neg" : r.impact > 8 ? "text-warn" : "text-slate-200"}`}>−{r.impact.toFixed(2)}%</td>
                  <td><div className="h-1.5 bg-ink rounded-sm"><div className="h-full rounded-sm" style={{ width: `${r.impact}%`, background: r.impact > 20 ? "#F43F5E" : r.impact > 8 ? "#F59E0B" : "#C026F5" }} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p className="text-xs text-fog mt-4 leading-relaxed max-w-lg">Each Pons token sits in its own Uniswap v4 pool paired against a tokenized stock, with a permanently locked LP. Depth stays thin, so a holder with a meaningful share of supply cannot exit through the pool without shoving its price off the stock's real quote — a move arbitrage bots immediately transmit across every pool referencing that stock. Cross settles the same block off-market at a flat negotiated price; no pool is touched.</p>
        </div>
      </div>
    </section>
  );
};
